import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, Clock, Printer } from "lucide-react";
import { Container, GlassCard } from "@/components/site/Primitives";
import { OnThisPageNav } from "@/components/site/OnThisPageNav";
import { CategoryPill } from "@/components/site/CategoryPill";
import { ArticleDocBody } from "@/components/doc/ArticleDocBody";
import { loadInsightBySlug, loadInsights, loadLeaderBySlug } from "@/lib/content-loader";
import { extractPageHeadings } from "@/lib/craft/headings";
import { insightDisplayLabel } from "@/lib/insights/categories";
import { formatPublicDateLong } from "@/lib/format-public-date";
import { buildOpenGraphMeta, buildTwitterMeta, absoluteMediaUrl, resolveSiteOrigin } from "@/lib/seo/meta";

export const Route = createFileRoute("/insights/$slug")({
  loader: async ({ params }) => {
    const [article, articles] = await Promise.all([
      loadInsightBySlug(params.slug),
      loadInsights(),
    ]);
    if (!article) throw notFound();
    const authorLeader = article.author.slug
      ? await loadLeaderBySlug(article.author.slug)
      : null;
    return { article, related: articles.filter((a) => a.slug !== params.slug), authorLeader };
  },
  head: ({ loaderData, params }) => {
    const a = loaderData?.article;
    const title = a ? `${a.title} — KS Lau & Co Insights` : "Insights — KS Lau & Co";
    const desc = a?.excerpt ?? "Insights from KS Lau & Co.";
    const origin = resolveSiteOrigin();
    const image = absoluteMediaUrl(origin, a?.cover);

    return {
      meta: [
        { title },
        { name: "description", content: desc },
        ...buildOpenGraphMeta({
          pathname: `/insights/${params.slug}`,
          title,
          description: desc,
          type: "article",
          image,
        }),
        ...buildTwitterMeta({ title, description: desc, image }),
        { property: "article:section", content: a?.category ?? "Insights" },
      ],
    };
  },
  component: ArticlePage,
  notFoundComponent: () => (
    <div className="container-page py-40 text-center">
      <h1 className="text-2xl font-bold text-primary">Article not found</h1>
      <Link to="/insights" className="mt-4 inline-block text-base font-semibold text-primary hover:underline">
        ← All insights
      </Link>
    </div>
  ),
  errorComponent: ({ reset }) => (
    <div className="container-page py-40 text-center">
      <h1 className="text-2xl font-bold text-primary">Something went wrong</h1>
      <button onClick={reset} className="mt-4 text-base font-semibold text-primary hover:underline">Try again</button>
    </div>
  ),
});

function ArticlePage() {
  const { article, related, authorLeader } = Route.useLoaderData();
  const craftState = (article as { craftState?: unknown }).craftState;
  const editorDoc = (article as { editorDoc?: unknown }).editorDoc;
  const pageHeadings = extractPageHeadings({
    editorDoc,
    craftState,
    body: article.body,
  });

  return (
    <div className="insight-print-document">
      <table className="print-layout-table">
        <thead>
          <tr>
            <td>
              <div className="print-letterhead print-only" aria-hidden="true">
                <div className="print-letterhead-brand">
                  <img src="/favicon-192.png" alt="" width={40} height={40} className="print-letterhead-logo" />
                  <div>
                    <p className="print-letterhead-firm">KS Lau &amp; Co</p>
                    <p className="print-letterhead-tagline">Chartered Accountants</p>
                  </div>
                </div>
                <div className="print-letterhead-meta">
                  <span className="print-letterhead-label">Insights</span>
                  <span>{formatPublicDateLong(article.date)}</span>
                </div>
              </div>
            </td>
          </tr>
        </thead>
        <tfoot>
          <tr>
            <td>
              <div className="print-footer print-only" aria-hidden="true">
                <p>
                  This article is for general information only and does not constitute professional advice. For guidance
                  specific to your circumstances, contact KS Lau &amp; Co at kslau.com.my
                </p>
                <p className="print-footer-url">www.kslau.com.my/insights/{article.slug}</p>
              </div>
            </td>
          </tr>
        </tfoot>
        <tbody>
          <tr>
            <td className="print-layout-content">
      <section className="insight-hero relative isolate overflow-hidden pb-10 pt-16 md:pb-12 md:pt-20">
        <div className="aurora">
          <div className="absolute -left-32 -top-32 h-[420px] w-[420px] rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute right-0 top-20 h-[420px] w-[420px] rounded-full bg-primary/5 blur-3xl" />
        </div>
        <Container>
          <div className="no-print flex flex-wrap items-center justify-between gap-3">
            <Link to="/insights" className="inline-flex items-center gap-2 text-base font-semibold text-primary hover:underline">
              <ArrowLeft className="h-4 w-4" /> All insights
            </Link>
            <button
              type="button"
              onClick={() => typeof window !== "undefined" && window.print()}
              className="inline-flex items-center gap-2 rounded-md border border-primary bg-transparent px-4 py-2 text-sm font-semibold text-primary transition hover:bg-primary hover:text-primary-foreground"
            >
              <Printer className="h-4 w-4" /> Print / Save as PDF
            </button>
          </div>
          <div className="mt-6 grid items-center gap-10 lg:grid-cols-[1.2fr_1fr]">
            <div className="insight-hero-copy max-w-3xl">
              <CategoryPill label={insightDisplayLabel(article)} size="sm" className="insight-print-category" />
              <h1 className="insight-print-title mt-4 text-3xl font-semibold leading-tight tracking-tight text-primary md:text-5xl">
                {article.title}
              </h1>
              {article.excerpt ? (
                <p className="insight-print-excerpt mt-5 text-lg leading-relaxed text-foreground/80 md:text-lg">
                  {article.excerpt}
                </p>
              ) : null}
              <div className="insight-print-byline mt-6 flex flex-wrap items-center gap-x-3 gap-y-2 text-base text-muted-foreground">
                <span>
                  By{" "}
                  <Link
                    to="/leadership/$slug"
                    params={{ slug: article.author.slug }}
                    className="font-semibold text-primary hover:underline"
                  >
                    {article.author.name}
                  </Link>
                  {article.author.designation ? (
                    <span className="print-author-designation">, {article.author.designation}</span>
                  ) : null}
                </span>
                <span className="print-meta-sep" aria-hidden="true">·</span>
                <span>{formatPublicDateLong(article.date)}</span>
                <span className="no-print" aria-hidden="true">·</span>
                <span className="no-print inline-flex items-center gap-1.5">
                  <Clock className="h-4 w-4" /> {article.readTime}
                </span>
              </div>
            </div>
            <figure className="insight-hero-cover overflow-hidden rounded-xl ring-1 ring-primary/10 shadow-[0_30px_80px_-30px_rgb(32_56_112/0.45)]">
              <img
                src={article.cover}
                alt={article.title}
                width={1280}
                height={720}
                className="aspect-[16/10] w-full object-cover"
              />
            </figure>
          </div>
        </Container>
      </section>

      <section className="insight-body relative pb-20 md:pb-28">
        <Container>
          <div className={`grid gap-10${pageHeadings.length ? " lg:grid-cols-[1fr_3fr]" : ""}`}>
            {pageHeadings.length > 0 ? (
              <OnThisPageNav headings={pageHeadings} className="no-print hidden lg:block" />
            ) : null}

            <article className="insight-print-body">
              <ArticleDocBody
                editorDoc={editorDoc}
                craftState={craftState}
                craftCustomCss={(article as { craftCustomCss?: string }).craftCustomCss}
                body={article.body}
              />

              {authorLeader && (
                <aside className="insight-print-author mt-14 pt-8">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    About the author
                  </p>
                  <div className="mt-4 flex items-start gap-5">
                    <img
                      src={authorLeader.image}
                      alt={`Portrait of ${authorLeader.name}`}
                      loading="lazy"
                      width={160}
                      height={200}
                      className="h-25 w-20 shrink-0 object-cover ring-1 ring-primary/15"
                    />
                    <div>
                      <h3 className="text-lg font-bold tracking-tight text-primary">{authorLeader.name}</h3>
                      <p className="mt-1 text-base leading-relaxed text-muted-foreground">
                        {authorLeader.qualifications}
                      </p>
                      <p className="mt-2 text-base font-semibold text-gold">{authorLeader.designation}</p>
                      <Link
                        to="/leadership/$slug"
                        params={{ slug: authorLeader.slug }}
                        className="no-print mt-3 inline-flex items-center gap-1.5 text-base font-semibold text-primary hover:underline"
                      >
                        View full profile <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    </div>
                  </div>
                </aside>
              )}
            </article>
          </div>

          <div className="no-print mx-auto mt-16 max-w-3xl">
            <GlassCard hover={false} className="p-8 md:p-10">
              <div className="grid items-center gap-5 md:grid-cols-[1.5fr_1fr]">
                <div>
                  <h3 className="text-xl font-bold tracking-tight text-primary">
                    Need help applying this to your business?
                  </h3>
                  <p className="mt-2 text-base text-muted-foreground">
                    Our partners can discuss the implications for your specific circumstances.
                  </p>
                </div>
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-5 py-3 text-base font-semibold text-primary-foreground hover:bg-primary/90 md:justify-self-end">
                  Make an enquiry <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </GlassCard>
          </div>

          <div className="no-print mx-auto mt-16 max-w-5xl">
            <h2 className="text-xl font-bold tracking-tight text-primary">More insights</h2>
            <div className="mt-6 grid gap-5 md:grid-cols-2">
              {related.map((a) => (
                <Link key={a.slug} to="/insights/$slug" params={{ slug: a.slug }} className="group">
                  <GlassCard className="flex h-full flex-col overflow-hidden p-0">
                    <img
                      src={a.cover}
                      alt={a.title}
                      loading="lazy"
                      width={1280}
                      height={720}
                      className="aspect-[16/9] w-full object-cover transition duration-700 group-hover:scale-[1.03]"
                    />
                    <div className="p-6">
                      <CategoryPill label={insightDisplayLabel(a)} />
                      <h3 className="mt-2 text-lg font-bold tracking-tight text-primary">{a.title}</h3>
                      <p className="mt-2 text-base leading-relaxed text-muted-foreground">{a.excerpt}</p>
                    </div>
                  </GlassCard>
                </Link>
              ))}
            </div>
          </div>
        </Container>
      </section>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
